import { Attributes } from "../../shared/types/attributes.type";

export type BillingEntityProps = Attributes<BillingEntity>;

export enum INVOICE_PARTNER {
  DIRECT = "Dir",
  PARTNER = "Part",
}

export class BillingEntity {
  private constructor(
    readonly invoiceNumber: string,
    readonly ticker: string,
    readonly partner: string,
    readonly concept: string,
    readonly period: string,
    readonly priceUnit: number,
    readonly units: number,
    readonly rate: number,
    readonly totalRate: number,
    readonly totalRateInvoice: number,
    readonly totalRateMargin: number,
    readonly discount: number,
    readonly currency: string,
    readonly currencyChange: number,
    readonly percentagePartner: number,
    readonly tax: number,
    readonly fp: string,
    readonly legalName: string,
    readonly cif: string,
    readonly idMyS?: number,
    readonly isVariable?: boolean,
    readonly invoicePartner?: boolean
  ) {}

  priceUnitFormatted(locale: string): string {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(this.priceUnit);
  }

  priceUnitPerClientFormatted(value: number, locale: string): string {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  unitsFormatted(locale: string): string {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(this.units);
  }

  rateFormatted(locale: string): string {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(this.rate);
  }

  discountFormatted(locale: string): string {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(this.discount * 100);
  }

  totalRateFormatted(locale: string): string {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(this.totalRate);
  }

  totalRateInvoiceFormatted(locale: string): string {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(this.totalRateInvoice);
  }

  totalRateMarginFormatted(locale: string): string {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(this.totalRateMargin);
  }

  currencyChangeFormatted(locale: string): string {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(this.currencyChange);
  }

  percentagePartnerFormatted(locale: string): string {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(this.percentagePartner * 100);
  }

  invoicePartnerFormatted(): string {
    if (typeof this.invoicePartner === "undefined") return "";
    return this.invoicePartner ? INVOICE_PARTNER.PARTNER : INVOICE_PARTNER.DIRECT;
  }

  idMySFormatted(): string {
    if (!this.idMyS) return "";
    return this.idMyS.toString();
  }

  taxFormatted(): string {
    if (!this.tax) return "";
    return `${this.tax}%`;
  }

  fullConceptFormatted(locale: string): string {
    return `${this.concept} ${this.ticker} ${this.priceUnitFormatted(locale)}x${this.unitsFormatted(
      locale
    )}`;
  }

  static of(props: BillingEntityProps) {
    return new BillingEntity(
      props.invoiceNumber,
      props.ticker,
      props.partner,
      props.concept,
      props.period,
      props.priceUnit,
      props.units,
      props.rate,
      props.totalRate,
      props.totalRateInvoice,
      props.totalRateMargin,
      props.discount,
      props.currency,
      props.currencyChange,
      props.percentagePartner,
      props.tax,
      props.fp,
      props.legalName,
      props.cif,
      props.idMyS,
      props.isVariable,
      props.invoicePartner
    );
  }
}
